<template>
  <!-- 任务进度 / 缺失资料弹窗：task 为 null 时关闭 -->
  <div class="overlay" :class="{ show: !!task }" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-head">
        <div class="modal-title">{{ task?.status === "missing" ? "需要补充案件材料" : "任务处理进度" }}</div>
        <button class="icon-btn" @click="$emit('close')">
          <svg class="icon sm"><use href="#jp-x" /></svg>
        </button>
      </div>
      <div v-if="task" class="modal-body">
        <!-- 缺失资料 -->
        <template v-if="task.status === 'missing'">
          <div class="material-summary">
            <template v-if="task.progress">{{ task.progress }}</template>
            <template v-else>
              系统已完成 {{ totalFiles(task) }} 份案件材料解析。缺少以下关键信息，补充后将自动重新解析并继续生成{{ task.type }}。
            </template>
          </div>
          <div class="missing-group">
            <div class="missing-title">必须补充</div>
            <div v-for="(m, i) in task.missingRequired || []" :key="m.name" class="missing-item">
              <div class="missing-index">{{ i + 1 }}</div>
              <div class="missing-main">
                <div class="missing-name">{{ m.name }}</div>
                <div class="missing-desc">{{ m.desc }}</div>
              </div>
              <span class="missing-tag">必须补充</span>
            </div>
          </div>
          <div v-if="(task.missingSuggested || []).length" class="missing-group">
            <div class="missing-title">建议补充</div>
            <div v-for="(m, i) in task.missingSuggested" :key="m.name" class="missing-item">
              <div class="missing-index">{{ i + 1 }}</div>
              <div class="missing-main">
                <div class="missing-name">{{ m.name }}</div>
                <div class="missing-desc">{{ m.desc }}</div>
              </div>
              <span class="missing-tag suggest">建议补充</span>
            </div>
          </div>
        </template>
        <!-- 处理进度 -->
        <template v-else>
          <div class="progress-block">
            <div class="progress-title">{{ task.progress }}</div>
            <div class="progress-desc">
              AI 正在读取案件材料、提取案件要素并核验材料完整性。处理期间可返回首页，任务会在后台继续运行。
            </div>
            <div class="progressbar">
              <span :style="{ width: (task.progressPct || 50) + '%' }"></span>
            </div>
          </div>
          <div class="missing-group">
            <div class="missing-title">当前处理阶段</div>
            <div class="missing-item">
              <div class="missing-index">✓</div>
              <div class="missing-main">
                <div class="missing-name">文件读取</div>
                <div class="missing-desc">已识别上传材料及基础文件信息。</div>
              </div>
            </div>
            <div class="missing-item">
              <div class="missing-index">{{ task.status === "parsing" ? "…" : "✓" }}</div>
              <div class="missing-main">
                <div class="missing-name">案件要素提取</div>
                <div class="missing-desc">提取当事人、请求事项、证据、事实与争议焦点。</div>
              </div>
            </div>
            <div class="missing-item">
              <div class="missing-index">{{ task.status === "generating" ? "…" : "—" }}</div>
              <div class="missing-main">
                <div class="missing-name">生成{{ task.type }}</div>
                <div class="missing-desc">基于完整案件材料生成结构化裁判文书。</div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="modal-actions">
        <template v-if="task?.status === 'missing'">
          <button class="btn" @click="$emit('close')">稍后处理</button>
          <button class="btn primary" @click="$emit('supplement', task)">上传补充材料</button>
        </template>
        <button v-else class="btn primary" @click="$emit('close')">知道了</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { totalFiles } from "../store";

defineProps({
  task: { type: Object, default: null },
});
defineEmits(["close", "supplement"]);
</script>

<style scoped>
.overlay {
  --brand: #1768ff;
  --brand-hover: #0e59db;
  --line-2: #f0f3f7;
}
.icon { width: 20px; height: 20px; stroke: currentColor; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.icon.sm { width: 16px; height: 16px; }
button { cursor: pointer; }
.icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  color: #60718a;
  display: grid;
  place-items: center;
}
.icon-btn:hover { background: #eef4ff; color: var(--brand); }
.btn {
  height: 40px;
  border: 1px solid #dce4f0;
  border-radius: 10px;
  background: #fff;
  color: #31435f;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 650;
  font-size: 14px;
  transition: 0.16s;
}
.btn:hover { background: #f8fbff; border-color: #c9d6e8; }
.btn.primary { background: var(--brand); border-color: var(--brand); color: #fff; }
.btn.primary:hover { background: var(--brand-hover); }

.overlay { position: fixed; inset: 0; background: rgba(18, 31, 53, 0.38); z-index: 100; display: none; align-items: center; justify-content: center; padding: 24px; }
.overlay.show { display: flex; }
.modal {
  width: min(680px, 92vw);
  max-height: 86vh;
  overflow: hidden;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 70px rgba(20, 36, 64, 0.25);
  border: 1px solid #e6ebf2;
  display: flex;
  flex-direction: column;
}
.modal-head { height: 58px; border-bottom: 1px solid var(--line-2); display: flex; align-items: center; justify-content: space-between; padding: 0 18px; flex: none; }
.modal-title { font-weight: 800; font-size: 17px; }
.modal-body { padding: 18px; overflow: auto; }
.modal-actions { padding: 14px 18px; border-top: 1px solid var(--line-2); display: flex; justify-content: flex-end; gap: 8px; flex: none; }
.progress-block { border: 1px solid #dce7fb; background: #f8fbff; border-radius: 13px; padding: 14px; }
.progress-title { font-weight: 760; font-size: 14px; }
.progress-desc { font-size: 12px; color: #6e7d93; margin-top: 6px; line-height: 1.65; }
.progressbar { height: 8px; border-radius: 999px; background: #e6edf7; overflow: hidden; margin-top: 12px; }
.progressbar span { display: block; height: 100%; background: var(--brand); border-radius: 999px; transition: width 0.35s; }
.missing-group { margin-top: 14px; }
.missing-title { font-size: 13px; font-weight: 800; color: #34475f; margin-bottom: 8px; }
.missing-item { display: flex; align-items: flex-start; gap: 10px; border: 1px solid #e8edf5; border-radius: 12px; padding: 11px 12px; margin-bottom: 8px; background: #fff; }
.missing-index { width: 24px; height: 24px; border-radius: 7px; background: #fff5e7; color: #ad6910; display: grid; place-items: center; font-size: 11px; font-weight: 800; flex: none; }
.missing-main { flex: 1; }
.missing-name { font-size: 13px; font-weight: 750; }
.missing-desc { font-size: 12px; color: #748198; line-height: 1.6; margin-top: 4px; }
.missing-tag { height: 22px; padding: 0 7px; border-radius: 999px; background: #fff5e7; color: #ad6910; font-size: 10px; display: flex; align-items: center; }
.missing-tag.suggest { background: #eef4ff; color: var(--brand); }
.material-summary { padding: 12px 13px; border-radius: 12px; background: #f7faff; border: 1px solid #dce7fb; color: #51657f; font-size: 12px; line-height: 1.65; }
</style>
